import React, { useState, useEffect } from 'react';
import { Globe, Plus, Trash2, ExternalLink, CheckCircle, AlertTriangle } from 'lucide-react';
import { getDomains, createDomain, deleteDomain, getSchools } from '../api';

export default function Domains() {
  const [domains, setDomains] = useState([]);
  const [schools, setSchools] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ school_id: '', domain: '', record_type: 'CNAME' });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState('');

  useEffect(() => {
    loadDomains();
    getSchools().then(r => setSchools(r.data.results || r.data)).catch(() => {});
  }, []);

  const loadDomains = () => {
    getDomains().then(r => setDomains(r.data.results || r.data || [])).catch(() => {});
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createDomain(form);
      setShowAdd(false);
      setForm({ school_id: '', domain: '', record_type: 'CNAME' });
      loadDomains();
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this domain record?')) return;
    setDeleting(id);
    try {
      await deleteDomain(id);
      loadDomains();
    } catch (e) { console.error(e); }
    finally { setDeleting(''); }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Domains</h2>
        <p>DNS and domain management for school sites</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h3><Globe size={18} /> Domain Records</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
            <Plus size={14} /> Add Domain
          </button>
        </div>

        {showAdd && (
          <div className="card-body" style={{ borderBottom: '1px solid var(--border)' }}>
            <form onSubmit={handleCreate}>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">School</label>
                  <select className="form-select" value={form.school_id} onChange={e => setForm({ ...form, school_id: e.target.value })} required>
                    <option value="">Select school...</option>
                    {schools.map(s => (
                      <option key={s.school_id} value={s.school_id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Domain</label>
                  <input className="form-input" value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="e.g. athletics.school.org" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Record Type</label>
                  <select className="form-select" value={form.record_type} onChange={e => setForm({ ...form, record_type: e.target.value })}>
                    <option value="CNAME">CNAME</option>
                    <option value="A">A Record</option>
                  </select>
                </div>
              </div>
              <div className="btn-group" style={{ marginTop: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={creating}>
                  {creating ? 'Adding...' : 'Add Domain'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Domain</th>
                <th>School</th>
                <th>Type</th>
                <th>SSL</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map(d => (
                <tr key={d.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="font-mono text-sm" style={{ fontWeight: 600 }}>{d.domain}</span>
                      <a href={`https://${d.domain}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', display: 'flex' }}>
                        <ExternalLink size={13} />
                      </a>
                    </div>
                  </td>
                  <td className="text-sm">{d.school_name || d.school_id}</td>
                  <td><span className="badge info">{d.record_type || 'CNAME'}</span></td>
                  <td>
                    {d.ssl_active ? (
                      <span style={{ color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <CheckCircle size={14} /> Active
                      </span>
                    ) : (
                      <span style={{ color: 'var(--yellow)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <AlertTriangle size={14} /> Pending
                      </span>
                    )}
                  </td>
                  <td><span className={`badge ${d.status || 'active'}`}>{d.status || 'active'}</span></td>
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(d.id)}
                      disabled={deleting === d.id}
                      style={{ padding: '4px 10px' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {domains.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <Globe size={40} />
                      <p>No domains configured</p>
                      <span className="sub">Add custom domains for your school sites</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
